import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  withAccountHeader,
  withAuthHeader,
  withRetries,
  compose,
  withE2ERequestStubbing,
  withConfiguredBaseUrl,
} from '../../../hooks/useService';
import urls from '../../../util/urls';
import { ServiceAuth } from '../../../hooks/useService/useService';
import { SupportedResourceTypes, DataSourceType } from '../util/types';
import queryString from 'query-string';
import _get from 'lodash/get';
import { idToFhirResourceTypes } from '../util/configuredResourceTypes';
import getQueryParameterSet, {
  getDateFilter,
} from './usePatientViewerFhirSearch/getQueryParameterSet';
import { ParameterHash } from '../util/PatientServiceParameters/ParameterSet';
import { SearchConfig } from './usePatientViewerFhirSearch';
import { snakeCase, cloneDeep, flatten, get } from 'lodash';
import FhirSearchService, {
  PaginationHandler,
} from '../../../services/FhirSearchService';
import moment from 'moment';
import { OntologySearchCode } from '../../OntologySearch/types';
import TerminologySearchService from '../../../services/TerminologyService';
import { ResolveTerminologyCodeId } from '../util/PatientServiceParameters/CodeFilter';

interface Options {
  axiosOptions?: AxiosRequestConfig;
  [key: string]: any;
}

const postQueryFetchConfig: AxiosRequestConfig = {
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    Accept: 'application/json',
  },
};

export type LinkRelation = {
  relation: 'self' | 'next';
  url: string;
};

export type FhirServiceEntry<T extends fhir.Resource> = {
  resource: T;
  fullUrl: string;
};

export type FhirServiceResponse<T extends fhir.Resource> = {
  resourceType: 'Bundle';
  link: LinkRelation[];
  type: 'searchset';
  entry: FhirServiceEntry<T>[];
};

export type SingleFhirSearchResult = {
  nextLink: string;
  results: FhirServiceEntry<fhir.Resource>[];
  resourceType: SupportedResourceTypes;
};

export type FhirSearchResult = SingleFhirSearchResult[] & { key?: string };

const parseFhirAPIResponse = (
  result: AxiosResponse<FhirServiceResponse<fhir.Resource>>,
  resourceType: SupportedResourceTypes,
): SingleFhirSearchResult => {
  const results = _get<
    FhirServiceResponse<fhir.Resource>,
    'entry',
    FhirServiceEntry<fhir.Resource>[]
  >(result.data, 'entry', []);
  const nextLinkRelation = _get<
    FhirServiceResponse<fhir.Resource>,
    'link',
    LinkRelation[]
  >(result.data, 'link', []).find(
    linkRelation =>
      _get<LinkRelation, 'relation', 'next' | 'self'>(
        linkRelation,
        'relation',
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        null,
      ) === 'next',
  );

  const nextLink = _get<LinkRelation, 'url', string>(
    nextLinkRelation,
    'url',
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    null,
  );
  return { results, nextLink, resourceType };
};

type FhirSearchReturnValue<TResource> = {
  _scroll_id?: string;
  hits: {
    hits: {
      _source: TResource;
    }[];
  };
};

class FhirSearchPaginationHandler<TResource>
  implements PaginationHandler<FhirSearchReturnValue<TResource>>
{
  private _pageIndex: number;
  private _baseQuery: any;
  private _pageSize: number;
  constructor(pageSize: number, baseQuery: any) {
    this._pageIndex = 0;
    this._baseQuery = baseQuery;
    this._pageSize = pageSize;
  }
  buildNextQuery = () => {
    const query = {
      ...this._baseQuery,
      limit: [
        {
          type: 'number',
          value: this._pageIndex * this._pageSize,
        },
        {
          type: 'number',
          value: this._pageSize,
        },
      ],
    };
    this._pageIndex++;
    return query;
  };
  hasNextPage = (response: FhirSearchReturnValue<TResource>) => {
    return response?.hits?.hits.length === this._pageSize;
  };
  mergeResponses = (
    response: FhirSearchReturnValue<TResource>[],
  ): FhirSearchReturnValue<TResource> => {
    const allHits = response.map(_ => _?.hits?.hits).filter(Boolean);
    const refResponse = cloneDeep(response[0]);
    refResponse.hits.hits = flatten(allHits);
    return refResponse;
  };

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  getQueryParams = (lastResponse: FhirSearchReturnValue<TResource>) => {
    return lastResponse?._scroll_id
      ? { scroll: lastResponse?._scroll_id }
      : { scroll: true };
  };
}

const addProjectToQuery = (
  projectId: string,
  parameters: ParameterHash = {},
): ParameterHash => {
  let _tag: string | string[] = `http://lifeomic.com/fhir/dataset|${projectId}`;
  if (parameters._tag) {
    // _tag already specified, add to request
    if (Array.isArray(parameters._tag)) {
      _tag = [...(parameters._tag as string[]), _tag];
    } else {
      _tag = [parameters._tag as string, _tag];
    }
  }

  return { ...parameters, _tag };
};

/**
 * Mimics legacy patient service sort.  FSS loads all (reasonably limited) data client-side, so server side sort isn't necessary
 * FSS does not support sorting on multiple fields, so client side sort is necessary
 */
const sortFSSResponse = (
  resourceType: SupportedResourceTypes,
  searchConfig: SearchConfig = {} as SearchConfig,
  resources: fhir.DomainResource[],
): fhir.DomainResource[] => {
  if (!searchConfig.applyDateFilter) return resources;

  const dateFilter = getDateFilter(resourceType, searchConfig);
  if (!dateFilter) return resources;

  const dateFields = [
    dateFilter.dateSelector,
    ...(dateFilter.periodSelector
      ? [
          `${dateFilter.periodSelector}.start`,
          `${dateFilter.periodSelector}.end`,
        ]
      : []),
  ];

  const sorted = resources
    .map(resource => {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const dateField = dateFields.find(field => !!get(resource, field));
      const rawDateValue = dateField ? get(resource, dateField) : null;
      const momentVal = rawDateValue ? moment(rawDateValue) : null;
      const compValue =
        momentVal && momentVal.isValid() ? momentVal.valueOf() : 0;
      return { resource, compValue };
    })
    .sort((a, b) => b.compValue - a.compValue)
    .map(_ => _.resource);
  return sorted;
};

export default class SearchService {
  getFHIRAPI: (auth: ServiceAuth) => AxiosInstance;
  constructor(_options?: Partial<Options>) {
    this.getFHIRAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
  }

  queryWithSearchConfig = async (
    auth: ServiceAuth,
    dataset: string,
    cohortId: string | null,
    searchConfig: SearchConfig,
    useFSSQuery: boolean,
  ): Promise<FhirSearchResult> => {
    const resourceTypesToFetch = idToFhirResourceTypes(
      _get<SearchConfig, 'dataSource', DataSourceType>(
        searchConfig,
        'dataSource',
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        null,
      ),
    );
    const fetchPromises = resourceTypesToFetch.map(resourceType => {
      const resolveTerminologyCodeId: ResolveTerminologyCodeId = async (
        ontologySearchCode: OntologySearchCode,
      ) => {
        const ontologyService = new TerminologySearchService();
        const ontologyCode = await ontologyService.searchGlobalCodings(auth)({
          code: ontologySearchCode.code,
          system: ontologySearchCode.system,
        });
        return ontologyCode?.data?.[0]?.id;
      };
      const queryParams = getQueryParameterSet(
        resourceType,
        searchConfig,
        resolveTerminologyCodeId,
      );
      const paramsWithProjectTag = addProjectToQuery(
        dataset,
        queryParams.fetchParameters,
      );
      if (cohortId) {
        paramsWithProjectTag.cohort = cohortId;
      }

      const fssQuery = async (): Promise<SingleFhirSearchResult> => {
        const service = new FhirSearchService();
        const searchMethod = cohortId
          ? service.searchCohort
          : !searchConfig.patientId
          ? service.searchDevice
          : service.searchPatient;
        const pageSize = searchConfig.pageSize || 200;
        const request: any = {
          type: 'select',
          columns: '*',
          from: [
            {
              table: snakeCase(resourceType),
            },
          ],
          limit: [
            {
              type: 'number',
              value: 0,
            },
            {
              type: 'number',
              value: pageSize,
            },
          ],
        };

        const fssClause = await queryParams.getFSSClause();
        if (fssClause) {
          request.where = {
            type: 'elasticsearch',
            query: fssClause,
          };
        }

        const rawFSSQueryResult = await searchMethod<
          FhirSearchReturnValue<fhir.DomainResource>
        >(auth)(
          dataset,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          cohortId || searchConfig.patientId,
          request,
          new FhirSearchPaginationHandler(pageSize, request),
        );
        const resources =
          rawFSSQueryResult.data?.hits?.hits?.map(_ => _._source) || [];
        const sortedResources = sortFSSResponse(
          resourceType,
          searchConfig,
          resources,
        );
        return {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          results: sortedResources.map(_ => ({
            resource: _,
            fullUrl: null,
          })),
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          nextLink: null,
          resourceType,
        };
      };

      const fhirQuery = async (): Promise<SingleFhirSearchResult> => {
        const requestData = queryString.stringify(paramsWithProjectTag);
        const rawFhirResult = await this.query(resourceType, auth, requestData);
        const parsedResult = parseFhirAPIResponse(rawFhirResult, resourceType);
        return parsedResult;
      };

      return useFSSQuery || searchConfig.deviceId !== undefined
        ? fssQuery()
        : fhirQuery();
    });

    const results: FhirSearchResult = await Promise.all(fetchPromises);
    results.key = searchConfig.key;
    return results;
  };

  loadMore = async (
    auth: ServiceAuth,
    previousResult: FhirSearchResult,
  ): Promise<FhirSearchResult> => {
    const fetchPromises = previousResult.map(
      (searchResult: SingleFhirSearchResult) => {
        // this resource type does not have more to load
        if (!searchResult.nextLink) return null;

        const urlParts = searchResult.nextLink.split('?');
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return this.query(searchResult.resourceType, auth, urlParts.pop());
      },
    );

    const loadMoreResults = await Promise.all(fetchPromises);

    const fhirSearchResults: FhirSearchResult = loadMoreResults.map(
      (loadMoreResult, index) => {
        const existingSearchState = previousResult[index];

        // there was not more to load for this resource type
        if (!loadMoreResult) return existingSearchState;

        const moreResultsState = parseFhirAPIResponse(
          loadMoreResult,
          existingSearchState.resourceType,
        );
        return {
          ...existingSearchState,
          results: [
            ...existingSearchState.results,
            ...moreResultsState.results,
          ],
          nextLink: moreResultsState.nextLink,
          resourceType: existingSearchState.resourceType,
        };
      },
    );

    return fhirSearchResults;
  };

  query = async (
    resourceType: SupportedResourceTypes,
    auth: ServiceAuth,
    query: string,
  ) => {
    switch (resourceType) {
      case 'Observation':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.observation({
            account: auth.account,
            isPostQuery: true,
          }),
          query,
          postQueryFetchConfig,
        );
      case 'MedicationStatement':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.medicationStatements({
            account: auth.account,
            isPostQuery: true,
          }),
          query,
          postQueryFetchConfig,
        );
      case 'MedicationAdministration':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.medicationAdministrations({
            account: auth.account,
            isPostQuery: true,
          }),
          query,
          postQueryFetchConfig,
        );
      case 'MedicationRequest':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.medicationRequests({
            account: auth.account,
            isPostQuery: true,
          }),
          query,
          postQueryFetchConfig,
        );
      case 'MedicationDispense':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.medicationDispense({
            account: auth.account,
            isPostQuery: true,
          }),
          query,
          postQueryFetchConfig,
        );
      case 'Immunization':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.immunization({
            account: auth.account,
            isPostQuery: true,
          }),
          query,
          postQueryFetchConfig,
        );
      case 'Procedure':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.procedure({ account: auth.account, isPostQuery: true }),
          query,
          postQueryFetchConfig,
        );
      case 'Encounter':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.encounter({ account: auth.account, isPostQuery: true }),
          query,
          postQueryFetchConfig,
        );
      case 'Condition':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.condition({ account: auth.account, isPostQuery: true }),
          query,
          postQueryFetchConfig,
        );
      case 'Goal':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.goal({ account: auth.account, isPostQuery: true }),
          query,
          postQueryFetchConfig,
        );
      case 'Sequence':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.sequence({ account: auth.account, isPostQuery: true }),
          query,
          postQueryFetchConfig,
        );
      case 'Media':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.media({ account: auth.account, isPostQuery: true }),
          query,
          postQueryFetchConfig,
        );
      case 'DocumentReference':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.documentReference({
            account: auth.account,
            isPostQuery: true,
          }),
          query,
          postQueryFetchConfig,
        );
      case 'DiagnosticReport':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.diagnosticReport({
            account: auth.account,
            isPostQuery: true,
          }),
          query,
          postQueryFetchConfig,
        );
      case 'ReferralRequest':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.referralRequest({
            account: auth.account,
            isPostQuery: true,
          }),
          query,
          postQueryFetchConfig,
        );
      case 'Specimen':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.specimen({ account: auth.account, isPostQuery: true }),
          query,
          postQueryFetchConfig,
        );
      case 'ImagingStudy':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.imagingStudy({
            account: auth.account,
            isPostQuery: true,
          }),
          query,
          postQueryFetchConfig,
        );
      case 'Consent':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.consent({ account: auth.account, isPostQuery: true }),
          query,
          postQueryFetchConfig,
        );
      case 'ProcedureRequest':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.procedureRequest({
            account: auth.account,
            isPostQuery: true,
          }),
          query,
          postQueryFetchConfig,
        );
      case 'Coverage':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.coverage({
            account: auth.account,
            isPostQuery: true,
          }),
          query,
          postQueryFetchConfig,
        );
      case 'ExplanationOfBenefit':
        return this.getFHIRAPI(auth).post(
          urls.api.fhir.explanationOfBenefit({
            account: auth.account,
            isPostQuery: true,
          }),
          query,
          postQueryFetchConfig,
        );
    }
  };
}
